import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEventReservationBooking,
  createReservation,
  getEventReservation,
} from '@/lib/api.ts';
import { ApiError } from '@/lib/ApiError.ts';
import { getApiErrorCode } from '@/lib/apiErrorCode.ts';
import { track, resolveBookingType } from '@/lib/analytics/index.ts';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import type { ActionRequiredResponse } from '@/lib/types.ts';
import {
  AuthAtom,
  useWidgetState,
  useResetState,
  useRestaurantId,
  ReservationAtom,
  EventReservationAtom,
  RestaurantAtom,
  PaymentMethodsByIdAtom,
  reloadRestaurantKey,
} from '../state.ts';
import { getActiveEventId } from '../eventFlow.ts';
import { isBankPaymentMethod } from '../paymentTypes.ts';

interface SubmitBookingOptions {
  stripe?: Stripe | null;
  useCredits?: boolean;
}

/**
 * Unified booking submission hook. Routes to the correct API
 * based on widget.bookingType and handles errors including 3D Secure.
 */
export function useSubmitBooking(options: SubmitBookingOptions = {}) {
  const [widget, updateWidget] = useWidgetState();
  const auth = useAtomValue(AuthAtom);
  const id = useRestaurantId();
  const navigate = useNavigate();
  const resetState = useResetState();
  const setReservation = useSetAtom(ReservationAtom);
  const setEventReservation = useSetAtom(EventReservationAtom);
  const restaurant = useAtomValue(RestaurantAtom);
  const methods = useAtomValue(PaymentMethodsByIdAtom);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (!auth?.token || !id) {
      setError('Authentication required.');
      return;
    }

    // Narrowed to `number` by the guard above; capture it so the nested
    // helpers don't need `id!` across the closure boundary.
    const restaurantId = id;
    const bookingType = resolveBookingType(widget.bookingType);

    setLoading(true);
    setError(null);
    setErrorCode(null);

    track('Widget Book Pressed', {
      restaurant_id: restaurantId,
      booking_type: bookingType,
    });

    // Report a failed booking once, from whichever error path we land in.
    function trackBookFailed(errorCode?: string) {
      track('Widget Book Failed', {
        restaurant_id: restaurantId,
        booking_type: bookingType,
        error_code: errorCode,
      });
    }

    // Capture payment method info before resetState() clears the widget
    function buildPaymentNavState() {
      const pm = widget.payment_method ? methods[widget.payment_method] : null;
      return {
        payment_method_id: widget.payment_method,
        ach_bank_name: widget.ach_bank_name,
        ach_bank_last4: widget.ach_bank_last4,
        card_brand: pm?.brand,
        card_last4: pm?.number,
        is_bank: (pm ? isBankPaymentMethod(pm) : false) || !!widget.ach_payment_id,
      };
    }

    try {
      if (widget.bookingType === 'restaurant') {
        await submitRestaurantBooking();
      } else if (widget.bookingType === 'event') {
        await submitEventBooking();
      } else {
        setError('Invalid booking type.');
      }
    } finally {
      setLoading(false);
    }

    async function submitRestaurantBooking() {
      if (!widget.reserve_body) {
        setError('Booking details are missing. Please go back and try again.');
        return;
      }

      const body = {
        ...widget.reserve_body,
        use_credits: options.useCredits ?? false,
        opt_in_accepted: widget.opt_in_accepted,
        ...(widget.referral_code && { referral_code: widget.referral_code }),
      };

      if (widget.payment_method) {
        body.payment_method_id = widget.payment_method;
      }

      try {
        // oxlint-disable-next-line no-console
        console.log('[useSubmitBooking] restaurant reserve_body:', JSON.stringify(body, null, 2));
        const response = await createReservation(auth!.token, body);
        // oxlint-disable-next-line no-console
        console.log('[useSubmitBooking] restaurant response:', JSON.stringify(response, null, 2));
        if (response.reservation) {
          setReservation(response.reservation);
          const uuid = response.reservation.uuid ?? response.reservation.confirmation_code;
          track('Widget Book Succeeded', {
            restaurant_id: restaurantId,
            booking_type: bookingType,
            reservation_uuid: uuid ?? undefined,
          });
          // Capture payment info before resetState() clears widget
          const paymentState = buildPaymentNavState();
          resetState();
          navigate(uuid ? `../confirmed/${uuid}` : '../confirmed', { state: paymentState });
        } else {
          // oxlint-disable-next-line no-console
          console.warn('[useSubmitBooking] No reservation in response:', response);
          trackBookFailed('no_reservation_in_response');
          setError('Booking was not confirmed. Please try again.');
        }
      } catch (e: unknown) {
        // SCA / 3D Secure on the reservation *charge* — the restaurant reserve
        // hits the same api charge() as events (via
        // ReservationRepository::handleIncompletePaymentException, rethrown by
        // PublicRestaurantReserveController), so it can also return
        // ActionRequired. Handle it before logging so the client secret carried
        // on the error never reaches the console.
        if (
          await handleActionRequired(e, async ({ actionRequired }) => {
            // Unlike the event reserve endpoint, restaurant reserve has NO
            // payment_id finalize path: the PaymentIntent is completed by
            // confirmCardPayment above, and the reservation is confirmed
            // server-side by the Stripe payment_intent.succeeded webhook (api
            // StripeEventListener -> ReservationRepository::processPaymentSuccess).
            // Navigate to the already-created reservation (uuid is in the
            // ActionRequired body); ConfirmedPage fetches it and shows a
            // processing state until the webhook confirms. Re-POSTing reserve
            // here would create a second reservation + charge.
            track('Widget Book Succeeded', {
              restaurant_id: restaurantId,
              booking_type: bookingType,
              reservation_uuid: actionRequired.reservation_uuid,
            });
            const paymentState = buildPaymentNavState();
            resetState();
            navigate(`../confirmed/${actionRequired.reservation_uuid}`, { state: paymentState });
          })
        ) {
          return;
        }
        // oxlint-disable-next-line no-console
        console.error('[useSubmitBooking] restaurant error:', e);
        handleBookingError(e, '../payment');
      }
    }

    async function submitEventBooking() {
      const eventId = getActiveEventId(widget);
      if (!eventId || !id) {
        setError('Event information is missing. Please go back and try again.');
        return;
      }

      const ticketId = widget.eventInfo?.ticketItemId;
      const quantity = Math.max(widget.eventInfo?.quantity ?? 1, 1);
      const ticketItems = Array.from({ length: quantity }, () => ticketId!);
      const addOnItems = widget.eventInfo?.addOnItemIds ?? [];
      const items = [...ticketItems, ...addOnItems];

      // The preview is fetched with use_credits defaulting to true on the server,
      // so widget.eventInfo.total_paid already has credits/fun coupons deducted.
      // When the user unchecks "Apply credits", we need to send the base total
      // (before credit deductions) so confirmed_total matches what the server
      // will calculate with use_credits=false.
      const useCredits = options.useCredits ?? false;
      let confirmedTotal = widget.eventInfo?.total_paid ?? 0;
      if (!useCredits) {
        // credits_used is in dollars (Money cast), points_used_value is raw
        // point count (100 points = $1)
        const creditsUsed = widget.eventInfo?.credits_used ?? 0;
        const pointsUsedDollars = (widget.eventInfo?.points_used_value ?? 0) / 100;
        confirmedTotal = confirmedTotal + creditsUsed + pointsUsedDollars;
      }

      const body = {
        items,
        confirmed_total: confirmedTotal,
        payment_method_id: widget.payment_method,
        skip_level_check: true,
        use_credits: useCredits,
        opt_in_accepted: widget.opt_in_accepted,
        ...(widget.referral_code && { referral_code: widget.referral_code }),
        ...(restaurant?.allow_alternative_pickup &&
          widget.alternative_pickup_first_name &&
          widget.alternative_pickup_phone &&
          widget.alternative_pickup_email && {
            alternative_pickup_name:
              `${widget.alternative_pickup_first_name} ${widget.alternative_pickup_last_name ?? ''}`.trim(),
            alternative_pickup_phone: widget.alternative_pickup_phone,
            alternative_pickup_email: widget.alternative_pickup_email,
          }),
      };

      try {
        const response = await createEventReservationBooking(auth!.token, body, eventId, id!);
        if (response.data) {
          setEventReservation(response.data);
          const uuid =
            ((response.data as Record<string, unknown>).uuid as string | undefined) ??
            ((response.data as Record<string, unknown>).confirmation_code as string | undefined);
          track('Widget Book Succeeded', {
            restaurant_id: restaurantId,
            booking_type: bookingType,
            reservation_uuid: uuid,
          });
          const paymentState = buildPaymentNavState();
          resetState();
          navigate(uuid ? `../confirmed/${uuid}` : '../confirmed', { state: paymentState });
        }
      } catch (e: unknown) {
        // SCA / 3D Secure on the reservation charge — shared with the
        // restaurant path via handleActionRequired.
        if (
          await handleActionRequired(e, async ({ paymentIntentId }) => {
            // Retry after 3D Secure: the event reserve endpoint accepts a
            // payment_id to return the reservation created on the first attempt
            // (api EventReserveController) — it does not re-charge.
            const retryBody = {
              payment_id: paymentIntentId,
              skip_level_check: true,
            };
            const retryResponse = await getEventReservation(auth!.token, retryBody, eventId, id!);
            if (retryResponse.data) {
              setEventReservation(retryResponse.data);
              const retryUuid =
                ((retryResponse.data as Record<string, unknown>).uuid as string | undefined) ??
                ((retryResponse.data as Record<string, unknown>).confirmation_code as
                  | string
                  | undefined);
              track('Widget Book Succeeded', {
                restaurant_id: restaurantId,
                booking_type: bookingType,
                reservation_uuid: retryUuid,
              });
              const retryPaymentState = buildPaymentNavState();
              resetState();
              navigate(retryUuid ? `../confirmed/${retryUuid}` : '../confirmed', {
                state: retryPaymentState,
              });
            }
          })
        ) {
          return;
        }

        handleBookingError(e, '../payment');
      }
    }

    function handleBookingError(e: unknown, paymentRoute: string) {
      trackBookFailed(
        getApiErrorCode(e) ?? (e instanceof ApiError ? e.type : 'unexpected_error')
      );
      if (
        e instanceof ApiError &&
        (e.type === 'ReserveErrorResponse' || e.type === 'CardErrorResponse')
      ) {
        updateWidget({ payment_error: (e as Error).message });
        navigate(paymentRoute);
      } else if (
        e instanceof ApiError &&
        (e.type === 'ReservationUnavailableErrorResponse' ||
          e.type === 'ReservationResourceNotFound' ||
          e.type === 'AlreadyCanceledReservationErrorResponse')
      ) {
        reloadRestaurantKey();
        updateWidget({
          availability: undefined,
          option: undefined,
          reservation_error: (e as Error).message,
        });
        navigate('../booking');
      } else {
        Sentry.captureException(e);
        setErrorCode(getApiErrorCode(e));
        setError('An unexpected error occurred. Please try again.');
      }
    }

    /**
     * Shared SCA / 3D-Secure handler for the reservation *charge*. This is
     * distinct from the card-tokenization 3DS the payment iframe already owns:
     * SCA can also fire on the charge, and both booking paths call the same api
     * `charge()`, which aborts with a 400 body of
     * `{ message: 'Payment processing requires action.', payment_intent_client_secret, reservation_uuid, … }`
     * (api ReservationRepository::handleIncompletePaymentException). Runs
     * `stripe.confirmCardPayment` on the (shadow-root) Stripe instance so the
     * challenge renders above the overlay, then defers to a path-specific
     * continuation to finalize the booking.
     *
     * The client secret is read exactly once (to pass to confirmCardPayment) and
     * is never logged or attached to a Sentry breadcrumb.
     *
     * @param e - The caught error; only ActionRequired errors are handled.
     * @param onAuthenticated - Path-specific finalize step, run after a
     *   successful challenge with the confirmed PaymentIntent id + response body.
     * @returns `true` when `e` was ActionRequired and has been fully handled
     *   (the caller must stop); `false` when unrelated (caller falls through to
     *   its normal error handling).
     */
    async function handleActionRequired(
      e: unknown,
      onAuthenticated: (ctx: {
        paymentIntentId: string | undefined;
        actionRequired: ActionRequiredResponse;
      }) => Promise<void>
    ): Promise<boolean> {
      if (!(e instanceof ApiError) || e.type !== 'ActionRequiredResponse') {
        return false;
      }

      const actionRequired = e.response as ActionRequiredResponse;

      // Telemetry for ticket-09 (SCA frequency): record only that SCA occurred
      // and the booking type. NEVER include payment_intent_client_secret — this
      // breadcrumb is shipped to Sentry.
      Sentry.addBreadcrumb({
        category: 'payment',
        type: 'info',
        level: 'info',
        message: 'SCA required on reservation charge',
        data: { booking_type: bookingType },
      });

      // Regional/connected Stripe accounts mint the PaymentIntent under a
      // different publishable key than the widget default; the ActionRequired
      // payload carries the correct one. Confirming a regional PaymentIntent
      // with the default key returns "No such payment_intent", so prefer the
      // payload's key (fall back to the default instance if it fails to load).
      const stripeForAction = actionRequired.stripe_public_key
        ? ((await loadStripe(actionRequired.stripe_public_key)) ?? options.stripe)
        : options.stripe;

      if (!actionRequired.payment_intent_client_secret || !stripeForAction) {
        trackBookFailed('action_required_no_client_secret');
        setError('Additional payment verification is required. Please try again.');
        Sentry.addBreadcrumb({
          category: 'payment',
          level: 'warning',
          message: 'SCA could not run (missing client secret or Stripe)',
          data: { booking_type: bookingType, outcome: 'unavailable' },
        });
        return true;
      }

      const result = await stripeForAction.confirmCardPayment(
        actionRequired.payment_intent_client_secret
      );

      if (result.error) {
        trackBookFailed(result.error.code ?? 'card_authentication_failed');
        setError(result.error.message ?? 'Payment authentication failed.');
        Sentry.captureException(result.error);
        Sentry.addBreadcrumb({
          category: 'payment',
          level: 'warning',
          message: 'SCA challenge failed',
          data: { booking_type: bookingType, outcome: 'failed' },
        });
        return true;
      }

      Sentry.addBreadcrumb({
        category: 'payment',
        level: 'info',
        message: 'SCA challenge succeeded',
        data: { booking_type: bookingType, outcome: 'succeeded' },
      });

      // Finalize after 3D Secure (path-specific). Shared retry-failure handling
      // matches the pre-extraction event-path behavior.
      try {
        await onAuthenticated({ paymentIntentId: result.paymentIntent?.id, actionRequired });
      } catch (retryError) {
        Sentry.captureException(retryError);
        trackBookFailed(getApiErrorCode(retryError) ?? 'retry_after_3ds_failed');
        setError('Booking failed after payment verification. Please try again.');
      }
      return true;
    }
  }, [
    auth?.token,
    id,
    widget.bookingType,
    widget.reserve_body,
    widget.payment_method,
    widget.eventInfo,
    widget.opt_in_accepted,
    widget.alternative_pickup_first_name,
    widget.alternative_pickup_last_name,
    widget.alternative_pickup_phone,
    widget.alternative_pickup_email,
    widget.referral_code,
    options.useCredits,
    options.stripe,
    navigate,
    resetState,
    updateWidget,
    setReservation,
    setEventReservation,
  ]);

  return { submit, loading, error, errorCode };
}
